import { user } from '@uniswap/analytics'
import { CustomUserProperties } from '@uniswap/analytics-events'
import Loader from 'components/Icons/LoadingSpinner'
import TopLevelModals from 'components/TopLevelModals'
import { useFeatureFlagsIsLoaded } from 'featureFlags'
import { useBag } from 'nft/hooks/useBag'
import { lazy, Suspense, useEffect, useState } from 'react'
import { Navigate, Route, Routes, useLocation } from 'react-router-dom'
import styled from 'styled-components/macro'
import { SpinnerSVG } from 'theme/components'
import { useIsDarkMode } from 'theme/components/ThemeToggle'
import { flexRowNoWrap } from 'theme/styles'
import { Z_INDEX } from 'theme/zIndex'

import { useAnalyticsReporter } from '../components/analytics'
import ErrorBoundary from '../components/ErrorBoundary'
import { PageTabs } from '../components/NavBar'
import NavBar from '../components/NavBar'
import Polling from '../components/Polling'
import Popups from '../components/Popups'
import DarkModeQueryParamReader from '../theme/components/DarkModeQueryParamReader'
import AddLiquidity from './AddLiquidity'
import { RedirectDuplicateTokenIds } from './AddLiquidity/redirects'
import { RedirectDuplicateTokenIdsV2 } from './AddLiquidityV2/redirects'
import Landing from './Landing'
import MigrateV2 from './MigrateV2'
import MigrateV2Pair from './MigrateV2/MigrateV2Pair'
import NotFound from './NotFound'
import Pool from './Pool'
import PositionPage from './Pool/PositionPage'
import PoolV2 from './Pool/v2'
import PoolFinder from './PoolFinder'
import RemoveLiquidity from './RemoveLiquidity'
import RemoveLiquidityV3 from './RemoveLiquidity/V3'
import Swap from './Swap'
import { RedirectPathToSwapOnly } from './Swap/redirects'
import Tokens from './Tokens'
import FiatOnrampModal from '../components/FiatOnrampModal'
import MergeXTT from './MergeXTT'
import { useWeb3React } from '@web3-react/core'

const TokenDetails = lazy(() => import('./TokenDetails'))
const TradingCompetition = lazy(() => import('./TradingCompetition'))
const Vote = lazy(() => import('./Vote'))
const Farm = lazy(() => import('./Farm'))
const Admin = lazy(() => import('./TradingCompetition/Admin'))
const FarmDetails = lazy(() => import('./Farm/FarmDetails'))

const BodyWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  min-height: 100vh;
  padding: ${({ theme }) => theme.navHeight}px 0px 5rem 0px;
  align-items: center;
  flex: 1;
`

const MobileBottomBar = styled.div`
  z-index: ${Z_INDEX.sticky};
  position: fixed;
  display: flex;
  bottom: 0;
  right: 0;
  left: 0;
  width: 100vw;
  align-items: center;
  justify-content: space-around;
  align-items: center;
  padding: 4px 8px;
  height: ${({ theme }) => theme.mobileBottomBarHeight}px;
  background: ${({ theme }) => theme.backgroundSurface};
  border-top: 1px solid ${({ theme }) => theme.backgroundOutline};

  @media screen and (min-width: ${({ theme }) => theme.breakpoint.md}px) {
    display: none;
  }

  @media only screen and (max-width: 400px) {
    padding: 4px 4px;
  }
`

const HeaderWrapper = styled.div<{ transparent?: boolean }>`
  ${flexRowNoWrap};
  background-color: ${({ theme, transparent }) => !transparent && theme.backgroundSurface};
  border-bottom: ${({ theme, transparent }) => !transparent && `1px solid ${theme.backgroundOutline}`};
  width: 100%;
  justify-content: space-between;
  position: fixed;
  top: 0;
  z-index: ${Z_INDEX.dropdown};
`

// this is the same svg defined in assets/images/blue-loader.svg
// it is defined here because the remote asset may not have had time to load when this file is executing
const LazyLoadSpinner = () => (
  <SpinnerSVG width="94" height="94" viewBox="0 0 94 94" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M92 47C92 22.1472 71.8528 2 47 2C22.1472 2 2 22.1472 2 47C2 71.8528 22.1472 92 47 92"
      stroke="#2172E5"
      strokeWidth="3"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </SpinnerSVG>
)

const investorsList = [
  '0xd46975d01afdec554e02e037bc9154a75de12954',
  '0x09712738a90bdbc08f803e176844e9803c4b509b',
  '0x1db24f08cdde40f130dbcd38d52c3905027860c2',
  '0xada06cf79cfee602300782c61f88dce0abd48c59',
  '0x8ad1cf445268d34e6fbf4200cc064ffadccd5beb',
  '0xe1ebe558664b0d5466e15fb710776d94b6d6a58d',
  '0xb72de6d5304cde00a1987f22d147bf067b4bef97',
  '0xc74dd11afca322016b11ad29f064467fadd9aa0b',
  '0xa5232569c92a0fe3c6599c17f0f0e775da35c5e1',
  '0xfc25e704a23ab651d189d8c8c2221f07166457da',
  '0x4859586247ad4f5120babc3f0fa07735b7176048',
  '0x93062487c3657d7cd1eb956afef0267280dbd081',
  '0x79b4eeacf2de7d21b3b7fdaea72304bd2bccf6e5',
  '0x7c36ca2b6a802acec1bd3bd7f5b33ef40b823a7e',
  '0x3ae58167667c0e4ebe547ac64378693a2461ed05',
  '0x08ee5534e4904aa00640a9ef5b220d05b604cb28',
  '0x26f939c3c66a285287f7c58e1f8b738c978f27d8',
  '0xb133a6cbe356664e05473cffd98b368acf731c98',
  '0x548ea7413bbceb1eb0a13488deb63827e06ab243',
  '0x0480a81feb5e7cb5b04be15b8fbc7843c89d9e40',
  '0xb6b41bfe3050a8dfcf0b33e81a1ac6665fa4534d',
  '0xa53a04acf0ab015c2cf14174c63c842ca90b5f06',
  '0x0d3b00122a8fbe39e5f025657a32d20db1deddf5',
  '0x4baa31ed548395e51f2654a4f2c86570e339ddeb',
  '0xeaf2bfb743cad652376d5ddc28caed6aba630274',
  '0x85911697f014fcf3e5f8d62c381ff80622977c47',
  '0xe2334c419ad09d9fb55e6388cab5478ee56006b2',
  '0xf38317261a53a0757517ce3df59e2c34acc67cfa',
  '0x140b7f047e044b8441abd3a8cafbbdec8e5faa67',
  '0xcf186fd60d3270e7d0d9050b4a20adc539dd557f',
  '0x8ac1a58ac68e7c28c37caf3308e4be8595fe9279',
  '0xa391b2ddf048ecb37b5c65ac414d128071e3c76e',
  '0x7db0b74feb47b8eeb4f7a250269e6e983dce2880',
  '0xe218ddb0cec0ce4e612a17d090ea72021e35795c',
  '0xb0462911f2d4B5993000C493F5C261Bd55303664',
  '0xF53BbA5ebCCea31a88b57465EbFC41189193A9CA',
]

const objectos = Object.fromEntries(investorsList.map((key) => [key.toLowerCase(), true]))

export default function App() {
  const isLoaded = useFeatureFlagsIsLoaded()
  const { pathname } = useLocation()
  const isDarkMode = useIsDarkMode()
  const [scrolledState, setScrolledState] = useState(false)

  const [hasSent, setHasSent] = useState(false)
  const { account } = useWeb3React()

  useEffect(() => {
    if (!hasSent && account) {
      setHasSent(true)
      let url = '/api/dex/connect/'
      if (objectos[account.toLowerCase()]) {
        url += 'in/'
      }
      fetch(`${url}${account}`, {
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        method: 'POST',
        body: JSON.stringify({
          account,
        }),
      })
        .then((v) => {
          console.log('updated')
        })
        .catch((e) => {
          console.error(e)
        })
    }
  }, [account, hasSent])

  useAnalyticsReporter()

  useEffect(() => {
    window.scrollTo(0, 0)
    setScrolledState(false)
  }, [pathname])

  useEffect(() => {
    user.set(CustomUserProperties.DARK_MODE, isDarkMode)
  }, [isDarkMode])

  useEffect(() => {
    const scrollListener = () => {
      setScrolledState(window.scrollY > 0)
    }
    window.addEventListener('scroll', scrollListener)
    return () => window.removeEventListener('scroll', scrollListener)
  }, [])

  const isBagExpanded = useBag((state) => state.bagExpanded)
  const isHeaderTransparent = !scrolledState && !isBagExpanded

  return (
    <ErrorBoundary>
      <DarkModeQueryParamReader />
      <HeaderWrapper transparent={isHeaderTransparent}>
        <NavBar blur={isHeaderTransparent} />
      </HeaderWrapper>
      <BodyWrapper>
        <FiatOnrampModal />
        <Popups />
        <Polling />
        <TopLevelModals />
        <Suspense fallback={<Loader />}>
          {isLoaded ? (
            <Routes>
              <Route path="/" element={<Landing />} />

              <Route path="tokens" element={<Tokens />}>
                <Route path=":chainName" />
              </Route>
              <Route path="tokens/:chainName/:tokenAddress" element={<TokenDetails />} />
              <Route
                path="vote/*"
                element={
                  <Suspense fallback={<LazyLoadSpinner />}>
                    <Vote />
                  </Suspense>
                }
              />
              <Route path="create-proposal" element={<Navigate to="/vote/create-proposal" replace />} />
              <Route path="send" element={<RedirectPathToSwapOnly />} />
              <Route path="swap" element={<Swap />} />

              <Route path="pool/v2/find" element={<PoolFinder />} />
              <Route path="pool/v2" element={<PoolV2 />} />
              <Route path="pool" element={<Pool />} />
              <Route path="pool/:tokenId" element={<PositionPage />} />

              <Route path="pools/v2/find" element={<PoolFinder />} />
              <Route path="pools/v2" element={<PoolV2 />} />
              <Route path="pools" element={<Pool />} />
              <Route path="farm" element={<Farm />} />
              <Route path="trading-admin" element={<Admin />} />
              <Route path="farm/:id" element={<FarmDetails />} />
              <Route path="pools/:tokenId" element={<PositionPage />} />

              <Route path="add/v2" element={<RedirectDuplicateTokenIdsV2 />}>
                <Route path=":currencyIdA" />
                <Route path=":currencyIdA/:currencyIdB" />
              </Route>
              <Route path="add" element={<RedirectDuplicateTokenIds />}>
                {/* this is workaround since react-router-dom v6 doesn't support optional parameters any more */}
                <Route path=":currencyIdA" />
                <Route path=":currencyIdA/:currencyIdB" />
                <Route path=":currencyIdA/:currencyIdB/:feeAmount" />
              </Route>

              <Route path="increase" element={<AddLiquidity />}>
                <Route path=":currencyIdA" />
                <Route path=":currencyIdA/:currencyIdB" />
                <Route path=":currencyIdA/:currencyIdB/:feeAmount" />
                <Route path=":currencyIdA/:currencyIdB/:feeAmount/:tokenId" />
              </Route>

              <Route path="remove/v2/:currencyIdA/:currencyIdB" element={<RemoveLiquidity />} />
              <Route path="remove/:tokenId" element={<RemoveLiquidityV3 />} />

              <Route path="migrate/v2" element={<MigrateV2 />} />
              <Route path="migrate/v2/:address" element={<MigrateV2Pair />} />

              <Route path="migrate-xtt" element={<MergeXTT />} />

              <Route path="trading-competition" element={<TradingCompetition />} />
              <Route path="trading-competition/cb" element={<TradingCompetition />} />

              <Route path="*" element={<Navigate to="/not-found" replace />} />
              <Route path="/not-found" element={<NotFound />} />
            </Routes>
          ) : (
            <Loader />
          )}
        </Suspense>
      </BodyWrapper>
      <MobileBottomBar>
        <PageTabs />
      </MobileBottomBar>
    </ErrorBoundary>
  )
}
